import React, { useState } from 'react';
import { FileText, Upload, XCircle, Trash2, Eye } from 'lucide-react';
import debtorDocumentApi from '../../../api/debtorDocumentApi';

interface DebtorDocumentsTabProps {
  debtorId: string;
  documents: any[];
  completeness: {
    complete: number;
    total: number;
    percentage: number;
  };
  onRefresh: () => void;
}

const REQUIRED_DOCUMENTS = [
  { type: 'ktp', label: 'KTP', description: 'Kartu Tanda Penduduk' },
  { type: 'kk', label: 'Kartu Keluarga', description: 'Kartu Keluarga' },
  { type: 'npwp', label: 'NPWP', description: 'Nomor Pokok Wajib Pajak' },
  { type: 'slip_gaji', label: 'Slip Gaji', description: 'Slip gaji 3 bulan terakhir' },
  {
    type: 'rekening_koran',
    label: 'Rekening Koran',
    description: 'Rekening koran 3 bulan terakhir',
  },
];

export default function DebtorDocumentsTab({
  debtorId,
  documents,
  completeness,
  onRefresh,
}: DebtorDocumentsTabProps) {
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isOtherDoc, setIsOtherDoc] = useState(false);

  // const docTypes = new Set(documents.map(d => d.document_type));
  // const requiredDocs = documents.filter(d =>
  //   REQUIRED_DOCUMENTS.some(r => r.type === d.document_type)
  // );
  const otherDocs = documents.filter(
    d => !REQUIRED_DOCUMENTS.some(r => r.type === d.document_type)
  );

  const handleUpload = (type: string, isOther = false) => {
    setSelectedType(type);
    setIsOtherDoc(isOther);
    setShowUploadModal(true);
  };

  const handleCreateDocument = async (docType: string, docName: string, docNumber: string) => {
    setUploading(true);
    try {
      await debtorDocumentApi.create({
        debtor_id: parseInt(debtorId),
        document_type: docType,
        document_name: docName,
        document_number: docNumber,
        status: 'active',
      });
      onRefresh();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId: number) => {
    if (!window.confirm('Yakin ingin menghapus dokumen ini?')) return;
    try {
      await debtorDocumentApi.delete(docId);
      onRefresh();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const getDocumentByType = (type: string) => {
    return documents.find(d => d.document_type === type);
  };

  return (
    <div className="bg-white border rounded-lg">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold">Manajemen Dokumen</h3>
        <p className="text-sm text-gray-500">
          Upload dan kelola dokumen debitur. {completeness.complete} dari {completeness.total}{' '}
          dokumen wajib sudah diupload.
        </p>
      </div>

      <div className="p-4 space-y-6">
        {/* Dokumen Wajib */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Dokumen Wajib</h4>
          <div className="grid md:grid-cols-3 gap-4">
            {REQUIRED_DOCUMENTS.map(doc => {
              const uploadedDoc = getDocumentByType(doc.type);
              const hasDoc = !!uploadedDoc;

              return (
                <div key={doc.type} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start gap-2 mb-2">
                    {hasDoc ? (
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-3 h-3 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{doc.label}</p>
                      <p className="text-sm text-gray-500">{doc.description}</p>
                    </div>
                  </div>

                  {hasDoc ? (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-gray-500">
                        {uploadedDoc.document_number || 'Terupload'}
                      </span>
                      <div className="flex gap-1">
                        {uploadedDoc.file_url && (
                          <button className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(uploadedDoc.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpload(doc.type)}
                      className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Dokumen Lainnya */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Dokumen Lainnya</h4>
            <button
              onClick={() => handleUpload('other', true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload Dokumen
            </button>
          </div>

          {otherDocs.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-4">
              {otherDocs.map(doc => (
                <div key={doc.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start gap-2 mb-2">
                    <FileText className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">{doc.document_name}</p>
                      <p className="text-sm text-gray-500">{doc.document_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="text-xs text-gray-500">{doc.document_number || '-'}</span>
                    <div className="flex gap-1">
                      {doc.file_url && (
                        <button className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border rounded-lg p-8 bg-gray-50 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Belum ada dokumen tambahan</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          docType={selectedType}
          docLabel={
            isOtherDoc ? '' : REQUIRED_DOCUMENTS.find(d => d.type === selectedType)?.label || ''
          }
          isOtherDoc={isOtherDoc}
          uploading={uploading}
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleCreateDocument}
        />
      )}
    </div>
  );
}

// Upload Modal Component
function UploadModal({
  docType,
  docLabel,
  isOtherDoc,
  uploading,
  onClose,
  onSubmit,
}: {
  docType: string;
  docLabel: string;
  isOtherDoc: boolean;
  uploading: boolean;
  onClose: () => void;
  onSubmit: (type: string, name: string, number: string) => void;
}) {
  const [docName, setDocName] = useState(docLabel);
  const [docNumber, setDocNumber] = useState('');
  const [customType, setCustomType] = useState('');

  const handleSubmit = () => {
    const finalType = isOtherDoc ? customType : docType;
    const finalName = isOtherDoc ? docName : docLabel;
    onSubmit(finalType, finalName, docNumber);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          {isOtherDoc ? 'Upload Dokumen Lainnya' : `Upload ${docLabel}`}
        </h3>

        <div className="space-y-4">
          {isOtherDoc && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jenis Dokumen
                </label>
                <input
                  type="text"
                  value={customType}
                  onChange={e => setCustomType(e.target.value)}
                  placeholder="Contoh: Surat Keterangan"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Dokumen</label>
                <input
                  type="text"
                  value={docName}
                  onChange={e => setDocName(e.target.value)}
                  placeholder="Nama dokumen"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nomor Dokumen</label>
            <input
              type="text"
              value={docNumber}
              onChange={e => setDocNumber(e.target.value)}
              placeholder="Contoh: 3201010101900001"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500 transition-colors">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Klik untuk upload file</p>
            <p className="text-xs text-gray-400">PDF, JPG, PNG (max 5MB)</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || (isOtherDoc && !customType)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {uploading ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  );
}
